import clsx from 'clsx';
import { fr } from '@/i18n/fr';
import { relative } from '@/lib/format';
import { Pill, type PillTone } from './core';

export type Health = 'operational' | 'degraded' | 'down' | 'unknown';

/**
 * Health is encoded by SHAPE first, colour second:
 *   operational = filled disc · degraded = half disc · down = cross · unknown = dashed ring.
 */
export function HealthGlyph({ health, size = 12, className }: { health: Health; size?: number; className?: string }) {
  const label = fr.health[health];
  const common = { width: size, height: size, viewBox: '0 0 12 12', role: 'img' as const, 'aria-label': label, className: clsx('shrink-0', className) };
  switch (health) {
    case 'operational':
      return (
        <svg {...common}>
          <title>{label}</title>
          <circle cx="6" cy="6" r="5" fill="var(--ok)" />
        </svg>
      );
    case 'degraded':
      return (
        <svg {...common}>
          <title>{label}</title>
          <circle cx="6" cy="6" r="4.4" fill="none" stroke="var(--warn)" strokeWidth="1.2" />
          <path d="M6 1.6a4.4 4.4 0 0 1 0 8.8z" fill="var(--warn)" />
        </svg>
      );
    case 'down':
      return (
        <svg {...common}>
          <title>{label}</title>
          <circle cx="6" cy="6" r="5" fill="var(--bad)" />
          <path d="M4 4l4 4M8 4l-4 4" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <title>{label}</title>
          <circle cx="6" cy="6" r="4.4" fill="none" stroke="var(--unknown)" strokeWidth="1.2" strokeDasharray="2 1.6" />
        </svg>
      );
  }
}

const healthTone: Record<Health, PillTone> = { operational: 'ok', degraded: 'warn', down: 'bad', unknown: 'neutral' };

export function HealthPill({ health, at }: { health: Health; at?: string | null }) {
  return (
    <Pill tone={healthTone[health]} icon={<HealthGlyph health={health} size={10} />} title={at ? `Dernier contrôle ${relative(at)}` : 'Aucun contrôle récent'}>
      {fr.health[health]}
    </Pill>
  );
}

const ENV_SHORT: Record<string, string> = { development: 'dev', staging: 'stg', production: 'prod', other: '' };

/**
 * Environment rail: one capsule per environment, health glyph + short name.
 * Production is outlined in the production colour so it is never mistaken.
 */
export function EnvRail({ envs }: { envs: { id: string; name: string; kind: string; health: Health; monitored?: boolean; inMaintenance?: boolean }[] }) {
  if (envs.length === 0) return <span className="text-caption text-faint">Aucun environnement</span>;
  return (
    <ul className="flex flex-wrap items-center gap-1" aria-label="Environnements">
      {envs.map((e) => (
        <li
          key={e.id}
          title={`${e.name} — ${fr.health[e.health]}${e.monitored === false ? ' (non surveillé)' : ''}${e.inMaintenance ? ' — maintenance' : ''}`}
          className={clsx(
            'flex h-6 items-center gap-1 rounded-full border pr-2 pl-1.5 text-caption',
            e.kind === 'production' ? 'border-prod/50 bg-prod-soft/50' : 'border-hairline bg-surface-2',
            e.health === 'unknown' && 'hatch',
          )}
        >
          <HealthGlyph health={e.health} size={11} />
          <span className={clsx('font-medium', e.kind === 'production' ? 'text-prod' : 'text-muted')}>{ENV_SHORT[e.kind] || e.name}</span>
        </li>
      ))}
    </ul>
  );
}

export function EnvChip({ kind, name }: { kind: string; name: string }) {
  return (
    <Pill tone={kind === 'production' ? 'prod' : kind === 'staging' ? 'accent' : 'neutral'} className={kind === 'production' ? 'ring-1 ring-prod/40' : undefined}>
      {kind === 'production' && <span aria-hidden className="size-1.5 rounded-full bg-prod" />}
      {name}
    </Pill>
  );
}

const statusTone: Record<string, PillTone> = {
  succeeded: 'ok',
  connected: 'ok',
  validated: 'ok',
  resolved: 'ok',
  partial: 'warn',
  incomplete: 'warn',
  pending: 'neutral',
  queued: 'neutral',
  untested: 'neutral',
  draft: 'neutral',
  prechecked: 'accent',
  running: 'accent',
  acknowledged: 'accent',
  scheduled: 'neutral',
  failed: 'bad',
  error: 'bad',
  revoked: 'bad',
  disconnected: 'bad',
  interrupted: 'bad',
  open: 'bad',
  cancelled: 'neutral',
  expired: 'neutral',
};

export function StatusPill({ status, label }: { status: string; label: string }) {
  return <Pill tone={statusTone[status] ?? 'neutral'}>{label}</Pill>;
}

export function SeverityPill({ severity }: { severity: 'info' | 'warning' | 'critical' }) {
  return <Pill tone={severity === 'critical' ? 'bad' : severity === 'warning' ? 'warn' : 'accent'}>{fr.severity[severity]}</Pill>;
}

/** "Source · date" provenance line required for every collected value (§5). */
export function Provenance({ source, at, staleAfterMs }: { source: string; at?: string | null; staleAfterMs?: number }) {
  if (!at) return <span className="text-caption text-faint">{source} — aucune donnée</span>;
  const stale = staleAfterMs !== undefined && Date.now() - new Date(at).getTime() > staleAfterMs;
  return (
    <span className={clsx('text-caption', stale ? 'text-warn' : 'text-faint')}>
      {source}, {relative(at)}
      {stale ? ' — données périmées' : ''}
    </span>
  );
}
